import { CommonModule } from "@angular/common";
import {
  Component, ElementRef,
  Input,
  OnDestroy,
  ViewEncapsulation, computed,
  effect,
  input,
  model,
  output,
  signal,
  viewChild
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { ClickFilterComponent, TriStateCheckBoxComponent } from '@myapp/features/widgets/components';
import { AuthorizationService } from '@myapp/modules/fwcore/services';
import { KENDO_SVGICON } from "@progress/kendo-angular-icons";
import { CheckBoxModule, TextBoxModule } from "@progress/kendo-angular-inputs";
import { KENDO_LISTVIEW, ListViewComponent } from '@progress/kendo-angular-listview';
import { SVGIcon, searchIcon } from '@progress/kendo-svg-icons';

import { LoaderModule } from '@progress/kendo-angular-indicators';
import { Subject } from "rxjs";
import { debounceTime, finalize, map, takeUntil } from "rxjs/operators";
import { ControlResizerOptions, ControlResizerService, OktaWrapperService } from "../../../../../../services/framework";
import { ImpersonationService } from '@myapp/modules/fwcore/services/impersonation.service';
import { RequestUserSummary, ResponseUserSummary, UserSummary, UserSummaryChecked } from "../../../../../modules/fwcore";
import { IsgLoaderComponent } from "../../../../../modules/fwcore/components/isg-loader/isg-loader.component";
import { AdminService } from '../../../services';

/**
 * Component for displaying and managing a list of users with filtering,
 * selection, and checkbox capabilities. Supports company-based access control
 * with flexible external user inclusion options.
 * 
 * Company filtering options:
 * - companyId: Internal users only (strict company membership)
 * - companyIdWithExternals: Internal users + external users with granted access
 * - companyIdExternalsOnly: Only external users with granted access
 */
@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, TextBoxModule, FormsModule, LoaderModule, IsgLoaderComponent
    , TriStateCheckBoxComponent, ClickFilterComponent, CheckBoxModule, KENDO_SVGICON, KENDO_LISTVIEW],
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class UserListComponent implements OnDestroy {

  //------------------------ Signal Input/Output/Model -------------------------

  /**
   * Filters to show internal users plus external users with granted access.
   * Returns users belonging to the specified company AND users from other
   * companies who have been granted access to work within that company.
   * 
   * Caller must have appropriate access permissions to the specified company.
   * Use this when you need the full picture of who can work within a company
   * context, including consultants and partners.
   * 
   * @see companyId for internal users only
   * @see companyIdExternalsOnly for external users only
   */
  public companyIdWithExternals = input<string | null>(null);

  /**
     * Filters to show only external users with granted access to a company.
     * Returns users from other companies who have been granted access to work
     * within the specified company. Does not include internal company users.
     * 
     * Caller must have appropriate access permissions to the specified company.
     * Use this when you need to see consultants and partners without internal staff.
     * 
     * @see companyId for internal users only
     * @see companyIdWithExternals for both internal and external users
     */
  public companyIdExternalsOnly = input<string | null>(null);

  /**
   * Filters by af_UserCompany relation rows for the specified company.
   * Returns users (regardless of their home company) who have a relation row
   * for this company. Unlike companyIdWithExternals, internal users are NOT
   * included by default — they only appear if they also have a relation row.
   * 
   * Use this when the relation table is the source of truth for membership
   * (e.g. "report managers for company X").
   * 
   * When combined with filterUserGroupCode, only matching relation rows are
   * considered. Takes precedence over the other company filters when set.
   */
  public companyIdGroupRelation = input<string | null>(null);

  /**
   * Filters users by group code for permission validation.
   * Used in conjunction with company filters for access control.
   */
  public filterUserGroupCode = input<string | null>(null);

  /**
   * Filter by user authorization code
   */
  public filterAuthAppCode = input<string | null>(null);

  /** Height of the control in pixels */
  public heightControl = model(300);

  /** Whether to display the toolbar containing search and filter controls */
  public showToolbar = input(false);

  /** Whether to display the license filter toggle in the toolbar */
  public showLicenseFilter = input(false);

  /** When true, includes email addresses in the returned user data */
  public includeEmail = input(false);

  /** Emits when user data has been loaded from the API */
  protected loadingPanelVisible = signal<boolean>(false);

  public loadedData = output<UserSummaryChecked[]>();

  /** Computed height for the ListView adjusted for toolbar visibility */
  public heightListView = computed(() => {

    if (this.showToolbar()) {

      let h = 0;

      // Take the difference between the top of the toolbar and the top of the listview, to get the height of the toolbar
      // As the license filter is not yet rendered, we need to calculate the height of the toolbar manually instead of using the height of the toolbar
      const tb = this.elem.nativeElement?.querySelector('.isg-userlist-toolbar')?.getBoundingClientRect();
      const ul = this.elem.nativeElement?.querySelector('.isg-userlist-lb-wrapper')?.getBoundingClientRect();

      const w = ul.top - tb.top;

      if (w) {
        h = w;
      }

      // reduce the height of the list by the height of the info panel
      return this.heightControl() - h;
    }
    else
      return this.heightControl();

  });

  //----------------------- Traditional Input/Output ---------------------------

  /** Module identifier for contextual user queries */
  public moduleId = input<string | null>(null);

  /**
   * Primary company filter for internal users only. When set, returns only
   * users whose company membership matches this ID. Does not include external
   * users who have been granted access.
   * 
   * If activeLicenseUser is true and licenseId is not set, auto-selects the
   * most recent active license for this company.
   * 
   * Note: Non-administrative users cannot query companies other than their own.
   * 
   * @see companyIdWithExternals for internal + external users
   * @see companyIdExternalsOnly for external users only
   */
  public companyId = input<string | null>(null);

  /**
   * Filters users assigned to a specific license. When set, only users with
   * license assignments are included. External users are exempt from this
   * filter as they maintain separate license arrangements.
   */
  public licenseId = input<string | null>(null);

  /**
   * License ID used to determine the assignedToLicense flag in results.
   * Each returned user will have assignedToLicense=true/false indicating
   * whether they're assigned to this specific license. Does not filter results.
   */
  public assignedTolicenseId = input<string | null>(null);

  /**
   * Filters users by role assignment. Includes users directly assigned to
   * the role, or users who inherit the role through license-wide assignments.
   */
  public roleId = input<string | null>(null);

  /** Whether to display checkboxes for multi-selection */
  public showCheckbox = input<boolean>(false);

  /** Whether to display an icon indicating user's license status */
  public showLicensedIcon = input<boolean>(false);

  /** Minimum height constraint for responsive layouts */
  public minHeight = input<number>(200);

  /** Width threshold below which dynamic height resizing is disabled */
  public widthThreshold = input<number>(600);

  /** IDs of currently checked items, synced bidirectionally with dataItems */
  @Input()
  set checkedIds(ids: string[]) {
    // Seed the cache from incoming IDs. We don't have full objects yet for these,
    // but they'll be filled in as data loads. Keep placeholder entries so the
    // cache reflects the intended selection.
    this.checkedCache.clear();
    ids.forEach(id => {
      this.checkedCache.set(id, { id, checked: true } as UserSummaryChecked);
    });

    // Reflect into currently visible items
    this.dataItems.forEach(item => {
      item.checked = this.checkedCache.has(item.id!);
    });
  }
  get checkedIds(): string[] {
    return Array.from(this.checkedCache.keys());
  }

  /**
   * When true, filters to show only users assigned to assignedTolicenseId.
   * Sets licenseId to assignedTolicenseId and activeLicenseUser to true.
   */
  @Input()
  set showLicenseUsersOnly(value: boolean) {

    if (value == true) {
      this.activeLicenseUser = true;
    }
    else {
      this.activeLicenseUser = false;
    }

    /**
     * Caution this was
     * 
      if (value == true) {
        this.licenseId = this.assignedTolicenseId;
        this.activeLicenseUser = true;
      }
      else {
        this.licenseId = null;
        this.activeLicenseUser = false;
      }
     * 
     */

    this.debouncedSubject.next();
  }
  get showLicenseUsersOnly(): boolean {

    return (!!(this.licenseId()) || this.activeLicenseUser);
  }

  /** Controls component initialization state for deferred data loading */
  @Input()
  set initialized(value: boolean) {

    this.hasInitialized = value;
  }
  get initialized(): boolean {

    return this.hasInitialized;
  }

  /** Emits the selected user when selection changes, null when cleared */
  public selected = output<UserSummary | null>();

  /** Emits whenever an individual item's checked state changes, with its new state already applied. */
  public itemCheckedChange = output<UserSummaryChecked>();

  /** When true, excludes the currently logged-in user from the list */
  public excludeCurrentUser = input<boolean>(false);


  //---------------------------- Constants -------------------------------------

  /** Search icon SVG for the filter input */
  protected readonly searchIcon: SVGIcon = searchIcon;

  //------------------------ ViewChild Properties ------------------------------

  /** Reference to the Kendo ListView for programmatic focus control */
  protected listview = viewChild(ListViewComponent);

  //------------------------- Component State ----------------------------------

  /** Subject for cleanup on component destruction */
  protected destroy$ = new Subject<void>();

  //-------------------------- Data Collections --------------------------------

  /** User summary items with added checked state for selection tracking */
  protected dataItems: UserSummaryChecked[] = [];

  //-------------------------- Lifecycle State ---------------------------------

  /** Tracks whether component has completed initialization */
  protected hasInitialized: boolean = false;

  //------------------------ Implementation State ------------------------------

  /** When true, filters to users with active license assignments */
  protected activeLicenseUser: boolean = true;

  /** Subject for debouncing rapid input changes before API calls */
  protected debouncedSubject = new Subject<void>();

  /** Current filter text for name/alias search */
  protected startsWithSearch: string = "";

  /** Source of truth for checked items across filter changes, keyed by id. */
  protected checkedCache = new Map<string, UserSummaryChecked>();

  //------------------------ Public Properties ---------------------------------

  /** Currently selected single user item */
  public selectedItem?: UserSummary | null;

  //---------------------------- Constructor -----------------------------------

  constructor(
    public serviceAuth: AuthorizationService
    , protected router: Router
    , protected adminService: AdminService
    , protected controlResizer: ControlResizerService
    , protected elem: ElementRef
    , protected oktaWrapper: OktaWrapperService
    , protected impersonationService: ImpersonationService
  ) {

    this.setupDebounceEffectHandlers();
  }

  //-------------------------- Public Methods ----------------------------------

  /**
   * Recalculates component height based on viewport and options
   * @param options - Optional control resizer configuration
   */
  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    let boundClientRect = this.elem.nativeElement.getBoundingClientRect()
    ctrlOptions.elemTop = boundClientRect.top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    if (this.showToolbar()) {
      ctrlOptions.elemTop = ctrlOptions.elemTop! + 91;
    }

    if (window.innerWidth > this.widthThreshold())
      this.heightControl.set(this.controlResizer.calculateHeight(ctrlOptions));
    else {
      // Do not dynamically resize height if below the width threshold
      this.heightControl.set(this.minHeight());
    }
  }

  /**
   * Marks the component as initialized, enabling data fetching
   */
  public Initialized(): void {

    this.hasInitialized = true;
  }

  /**
   * Clears the visible list, current selection, and checked state cache.
   * Useful for consumers that need to reset the component when preconditions
   * for fetching are no longer met.
   */
  public clear(): void {

    this.dataItems = [];
    this.selectedItem = null;
    this.checkedCache.clear();
    this.startsWithSearch = "";
  }

  /**
   * Clear the selection
   */
  public clearSelection() {

    this.selectedItem = null;
    this.listview()?.focus(undefined);
  }

  /**
   * Clears all checked items in the list
   */
  public clearCheckedItems() {
    this.checkedCache.clear();
    this.dataItems.forEach(item => item.checked = false);
  }

  /**
   * Returns all currently checked user items
   * @returns Array of checked UserSummary objects
   */
  public getCheckedItems(): UserSummary[] {
    return Array.from(this.checkedCache.values());
  }

  /**
     * Returns display names of all checked users
     * @returns Array of display name strings
     */
  public getCheckedNames(): string[] {
    return Array.from(this.checkedCache.values())
      .map(item => item.displayName!)
      .filter(n => !!n);
  }

  /**
   * Removes specified items from the local data collection
   * @param items - Array of UserSummary items to remove
   */
  public removeItems(items: UserSummary[] | null) {

    const t = this.dataItems.length ?? 0;

    for (let i = t - 1; i >= 0; i--) {

      let v = this.dataItems[i];

      if (items?.includes(v))
        this.dataItems.splice(i, 1);
    }
  }

  /**
   * Triggers a data refresh from the API
   */
  public refresh(checkedIds: string[] | null) {
    if (checkedIds) {
      this.checkedIds = checkedIds; // routes through the setter, which updates the cache
    }
    this.debouncedSubject.next();
  }

  //------------------------- Lifecycle Methods --------------------------------

  ngOnDestroy(): void {

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes debounce handlers for API call throttling
   */
  protected setupDebounceEffectHandlers(): void {

    // Debounce the get data call
    this.debouncedSubject.pipe(
      debounceTime(300),  // Adjust the debounce time as needed
      takeUntil(this.destroy$) // Cleanup when component is destroyed
    ).subscribe(() => this.getUserSummaries());

    // Single effect watching all signal-based filter inputs.
    // Replaces the old ngOnChanges + inputChanges() orchestration.
    effect(() => {

      // Read all relevant signal values to establish dependencies
      this.moduleId();
      this.companyId();
      this.companyIdWithExternals();
      this.companyIdExternalsOnly();
      this.companyIdGroupRelation();
      this.licenseId();
      this.roleId();
      this.filterUserGroupCode();
      this.filterAuthAppCode();

      // Trigger debounced refresh when any of these change
      if (this.hasInitialized) {
        this.debouncedSubject.next();
      }
    });
  }


  // ------------------- Loader -----------------------------

  protected loaderShow() {
    this.loadingPanelVisible.set(true);
  }

  protected loaderClose() {
    this.loadingPanelVisible.set(false);
  }

  //--------------------------- Event Handlers ---------------------------------


  protected onCheckChange(item: UserSummaryChecked): void {
    if (item.checked) {
      this.checkedCache.set(item.id!, item);
    } else {
      this.checkedCache.delete(item.id!);
    }

    this.itemCheckedChange.emit(item);
  }

  /**
   * Forces the given item back to checked and updates the internal cache - used by consumers
   * that need to veto an uncheck (e.g. a required/locked user) as soon as it happens.
   * Replaces both the item and the array reference, since mutating item.checked in place
   * isn't reliably picked up by the ListView's rendering.
   */
  public forceChecked(item: UserSummaryChecked): void {

    const index = this.dataItems.findIndex(i => i.id === item.id);

    if (index < 0)
      return;

    const updated: UserSummaryChecked = { ...this.dataItems[index], checked: true };
    this.dataItems = [
      ...this.dataItems.slice(0, index),
      updated,
      ...this.dataItems.slice(index + 1)
    ];
    this.checkedCache.set(updated.id!, updated);
  }

  /**
   * Handles item click for selection toggling
   * @param index - ListView index of clicked item
   * @param dataItem - The clicked user summary
   * @param event - Click event for propagation control
   */
  protected onClickItemEvent(index: number, dataItem: UserSummary, event: Event): void {

    let newSelection: UserSummary = dataItem;

    if (this.selectedItem == newSelection) {
      this.clearSelection();
      this.selected.emit(this.selectedItem);
    }
    else {
      this.selectedItem = newSelection;
      this.listview()?.focus(index);
      this.selected.emit(this.selectedItem);
    }

    event.stopPropagation();
  }

  /**
   * Handles ListView change events for checkbox state changes
   * @param event - The change event
   */
  protected onChangeListView(event: any) {

    // will capture items being checked
  }

  /**
   * Handles ListView container click events
   * @param event - The click event
   */
  protected onClickListView(event: any) {

  }

  /**
   * Handles search filter input changes with debouncing
   * @param query - The filter query string
   */
  protected onFilterChange(query: string): void {

    this.startsWithSearch = query.toLowerCase();
    this.debouncedSubject.next();
  }

  //------------------------- Internal Methods ---------------------------------

  //-------------------------- API Methods -------------------------------------

  /**
   * Fetches user summaries from the API.
   * 
   * Company filtering behavior (mutually exclusive, first match wins):
   * - companyIdExternalsOnly: Only external users with granted access
   * - companyIdWithExternals: Internal users + external users with granted access
   * - companyId: Internal users only (strict company membership)
   * - None specified: Defaults to caller's own company (internal only)
   * 
   * External users are exempt from license filtering as they maintain
   * separate license arrangements with their own organizations.
   */
  protected getUserSummaries(): void {

    if (!this.hasInitialized)
      return;

    this.loaderShow();

    const withExternals = this.companyIdWithExternals();
    const externalsOnly = this.companyIdExternalsOnly();
    const groupRelation = this.companyIdGroupRelation();
    const filterUserGroupCode = this.filterUserGroupCode();
    const filterAuthAppCode = this.filterAuthAppCode();

    // Determine if company name should be included based on company filter usage
    const hasCompanyFilter = (withExternals != null && withExternals.length > 0)
      || (externalsOnly != null && externalsOnly.length > 0)
      || (groupRelation != null && groupRelation.length > 0);

    const req: RequestUserSummary = {
      moduleId: this.moduleId()
      , companyId: this.companyId()
      , companyIdWithExternals: withExternals
      , companyIdExternalsOnly: externalsOnly
      , companyIdGroupRelation: groupRelation
      , licenseId: this.licenseId() ?? (this.activeLicenseUser ? this.assignedTolicenseId() : null) //caution this was just license id
      , roleId: this.roleId()
      , activeUser: true
      , activeLicenseUser: this.activeLicenseUser
      , assignedToLicenseId: this.assignedTolicenseId()
      , startsWith: (this.startsWithSearch
        && this.startsWithSearch.length > 0) ? this.startsWithSearch : null
      , includeEmail: this.includeEmail()
      , includeCompany: hasCompanyFilter
      , userGroupCode: filterUserGroupCode
      , intAppCode: filterAuthAppCode
    };

    this.serviceAuth.getData$<RequestUserSummary, ResponseUserSummary>(req, this.serviceAuth.endpointUserSummary)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.loaderClose(); }),
        map((resp) => {
          // Filter out the active identity if excludeCurrentUser is true — the
          // impersonated user when impersonating, otherwise the logged-in user.
          const activeEmail = this.impersonationService.get() ?? this.oktaWrapper.getUser()?.email;
          const currentEmail = this.excludeCurrentUser() ? activeEmail : null;
          const updatedItems: UserSummaryChecked[] = (resp.data ?? [])
            .filter((item: UserSummary) => !currentEmail || item.email?.toLocaleLowerCase() !== currentEmail.toLocaleLowerCase())
            .map((item: UserSummary) => {
              const isChecked = this.checkedCache.has(item.id!);
              const enriched: UserSummaryChecked = { ...item, checked: isChecked };

              // Backfill cache with full object data now that we have it
              if (isChecked) {
                this.checkedCache.set(item.id!, enriched);
              }

              return enriched;
            }) ?? [];

          return updatedItems;
        })
      )
      .subscribe(
        (items) => {
          this.dataItems = items;

          this.loadedData.emit(this.dataItems);
        }
      );
  }

}
